import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Button } from '@duck/ui'
import { RocketLaunchIcon } from '@heroicons/react/24/outline'
import { Plus } from 'lucide-react'
import { useTar } from '@/modules/i18n/useTar'
import { useAuth } from '@/hooks/useAuth'
import { useDebouncedEffect } from '@/hooks/useDebouncedEffect'
import { useReleaseNotes } from '@/hooks/useReleaseNotes'
import { ReleaseNoteDialog } from '@/components/ReleaseNotes/ReleaseNoteDialog'
import { ReleaseNote, getLabels, UNREAD_GLOW_DURATION_MS } from '@/components/ReleaseNotes/ReleaseNote'
import { ReleaseNotesTimelineSkeleton } from '@/components/ReleaseNotes/ReleaseNotesPageSkeleton'
import { apis } from '@/modules/apiClients'
import { toast } from 'sonner'
import type { UserService } from '@duck/api-definitions'
import { cn } from '@duck/ui'

const MS_ONE_DAY = 24 * 60 * 60 * 1000

function isRecent(dateStr: string, withinDays = 7): boolean {
  return Date.now() - new Date(dateStr).getTime() < withinDays * MS_ONE_DAY
}

function formatTimelineDate(dateStr: string, locale: string): string {
  return new Date(dateStr).toLocaleDateString(locale, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).toUpperCase()
}

interface ReleaseNotesPageProps {
  scrollToSlug?: string
}

export const ReleaseNotesPage = ({ scrollToSlug }: ReleaseNotesPageProps) => {
  const { t, locale } = useTar()
  const navigate = useNavigate()
  const { isAdmin } = useAuth()
  const { notes, loading, fetchNotes, markAsRead, isBlocked } = useReleaseNotes(false, true)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  useEffect(() => {
    if (isBlocked) {
      navigate({ to: '/' })
    }
  }, [isBlocked, navigate])

  useDebouncedEffect(
    () => {
      if (notes.some((n) => n.unread)) markAsRead()
    },
    [notes],
    UNREAD_GLOW_DURATION_MS
  )
  const [editNote, setEditNote] = useState<UserService.ReleaseNote | null>(null)

  useEffect(() => {
    if (!scrollToSlug || notes.length === 0) return
    const el = document.getElementById(scrollToSlug)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [scrollToSlug, notes])

  const displayNotes = useMemo(
    () => notes.filter((n) => !n.deletedAt),
    [notes]
  )

  const notesByDate = useMemo(() => {
    const map = new Map<string, UserService.ReleaseNote[]>()
    for (const note of displayNotes) {
      const date = new Date(note.noteDate)
      const key = [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, '0'),
        String(date.getDate()).padStart(2, '0'),
      ].join('-')
      const existing = map.get(key)
      if (existing) existing.push(note)
      else map.set(key, [note])
    }
    const sorted = Array.from(map.entries()).sort(
      ([a], [b]) => (b as string).localeCompare(a as string)
    )
    return sorted
  }, [displayNotes])

  const handleDelete = async (id: string) => {
    if (!confirm(t('releaseNotes.confirmDelete'))) return
    try {
      await apis.userService.releaseNotesIdDelete(id)
      toast.success(t('releaseNotes.deleted'))
      fetchNotes(true)
    } catch (error) {
      console.error('Failed to delete release note:', error)
      toast.error(t('releaseNotes.deleteError'))
    }
  }

  const handleToggleActive = async (note: UserService.ReleaseNote) => {
    const nextActive = !(note.active ?? true)
    try {
      await apis.userService.releaseNotesIdPut(note.id, {
        slug: note.slug ?? note.id,
        title: note.title,
        body: note.body,
        noteDate: note.noteDate,
        labels: getLabels(note),
        active: nextActive,
      })
      toast.success(nextActive ? t('releaseNotes.updated') : t('releaseNotes.updated'))
      fetchNotes(true)
    } catch (error) {
      console.error('Failed to update release note:', error)
      toast.error(t('releaseNotes.updateError'))
    }
  }

  if (displayNotes.length === 0 && !loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto py-8 px-4">
          <header className="flex items-start justify-between gap-3 mb-8">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-primary/10 p-2.5 text-primary shrink-0">
                <RocketLaunchIcon className="h-7 w-7" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">{t('releaseNotes.whatsNew')}</h1>
                <p className="text-muted-foreground mt-1">{t('releaseNotes.subtitle')}</p>
              </div>
            </div>
            {isAdmin && (
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                {t('releaseNotes.createNew')}
              </Button>
            )}
          </header>
          <p className="text-muted-foreground text-center py-12">{t('releaseNotes.noReleaseNotes')}</p>
        </div>
        {isAdmin && (
          <ReleaseNoteDialog
            open={createDialogOpen}
            onOpenChange={setCreateDialogOpen}
            mode="create"
            onSuccess={async () => { await fetchNotes(true) }}
          />
        )}
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background w-full">
      <div className="pt-4 md:p-8">
        {/* Header */}
        <header className="flex items-start justify-between gap-3 mb-8">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-primary/10 p-2.5 text-primary shrink-0">
              <RocketLaunchIcon className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">{t('releaseNotes.whatsNew')}</h1>
              <p className="text-muted-foreground mt-1">{t('releaseNotes.subtitle')}</p>
            </div>
          </div>
          {isAdmin && (
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              {t('releaseNotes.createNew')}
            </Button>
          )}
        </header>

        {/* Spacing between header and timeline */}
        <div className="h-2 mb-2" />

        {/* Timeline */}
        <div className="space-y-10">
          {loading ? (
            <ReleaseNotesTimelineSkeleton />
          ) : (
            notesByDate.map(([dateKey, dayNotes]) => {
              const recent = isRecent(dayNotes[0].noteDate)
              return (
                <div key={dateKey}>
                  {/* Date row with dot */}
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-xs text-muted-foreground font-medium whitespace-nowrap">
                      {formatTimelineDate(dayNotes[0].noteDate, locale)}
                    </span>
                    <div
                      className={cn(
                        'h-2.5 w-2.5 rounded-full shrink-0',
                        recent ? 'bg-primary' : 'bg-muted-foreground/40'
                      )}
                    />
                    <div className="flex-1 h-px bg-border" />
                  </div>

                  {/* Cards with colored left line */}
                  <div className="space-y-5 relative">
                    {dayNotes.map((note) => (
                      <ReleaseNote
                        key={note.id}
                        note={note}
                        isAdmin={isAdmin}
                        onEdit={setEditNote}
                        onDelete={handleDelete}
                        onToggleActive={handleToggleActive}
                      />
                    ))}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
      {isAdmin && (
        <>
          <ReleaseNoteDialog
            open={createDialogOpen}
            onOpenChange={setCreateDialogOpen}
            mode="create"
            onSuccess={async () => { await fetchNotes(true) }}
          />
          <ReleaseNoteDialog
            open={!!editNote}
            onOpenChange={(open) => !open && setEditNote(null)}
            mode="edit"
            note={editNote}
            onSuccess={async () => {
              await fetchNotes(true)
              setEditNote(null)
            }}
          />
        </>
      )}
    </div>
  )
}
